// SCR-STAFF-1202 — فیلدهای سفارشی (US-CFG-020): تعریف فیلد برای موجودیت‌های مجاز Patient، Lead، Service و Supplier با انواع
// US-PAT-006 (متن، عدد، تاریخ، انتخاب تکی و چندتایی، بلی/خیر)، حساسیت (داخلی، شخصی، حساس — هرگز بالینی: AC-CFG-020.1)، الزامی
// بودن و نمایش در ثبت سریع؛ غیرفعال‌سازی به‌جای حذف (داده ثبت‌شده باقی می‌ماند). کلید پس از ایجاد تغییر نمی‌کند.
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import {
  unwrap,
  type CustomFieldDefinitionInfo,
  type CustomFieldEntity,
  type CustomFieldOption,
  type CustomFieldSensitivity,
  type CustomFieldType,
} from '@phoenix/api-client';
import { useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  CheckboxRHF,
  DataTable,
  ErrorState,
  FormDialog,
  ListPage,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { WriteNotice } from '../settings/common';
import { cfgKeys } from './common';

export const SCR_STAFF_1202 = 'SCR-STAFF-1202';

export const CUSTOM_FIELD_ENTITIES: readonly CustomFieldEntity[] = ['Patient', 'Lead', 'Service', 'Supplier'];
export const CUSTOM_FIELD_TYPES: readonly CustomFieldType[] = [
  'Text',
  'Number',
  'Date',
  'SingleSelect',
  'MultiSelect',
  'Boolean',
];
export const CUSTOM_FIELD_SENSITIVITIES: readonly CustomFieldSensitivity[] = [
  'Internal',
  'Personal',
  'Sensitive',
];
const KEY = /^[a-z][a-z0-9_]{1,63}$/;

interface FieldForm {
  key: string;
  label: string;
  type: CustomFieldType;
  options: string;
  isRequired: boolean;
  sensitivity: CustomFieldSensitivity;
  showInQuickRegister: boolean;
  sortOrder: string;
}

/** هر خط «مقدار=برچسب» (یا فقط برچسب که مقدار هم می‌شود). */
export function parseOptions(text: string): CustomFieldOption[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, ...label] = line.split('=');
      const trimmed = value!.trim();
      return { value: trimmed, label: label.join('=').trim() || trimmed };
    });
}

function FieldDialog({ entity, onClose }: { entity: CustomFieldEntity; onClose: () => void }) {
  const { t } = useTranslation('cfg');
  const { tenantApi } = useTenantSession();
  const queryClient = useQueryClient();
  const { control, handleSubmit, formState, setError } = useForm<FieldForm>({
    defaultValues: {
      key: '',
      label: '',
      type: 'Text',
      options: '',
      isRequired: false,
      sensitivity: 'Internal',
      showInQuickRegister: false,
      sortOrder: '0',
    },
  });
  const type = useWatch({ control, name: 'type' });
  const [fieldError, setFieldError] = useState(false);
  const selectable = type === 'SingleSelect' || type === 'MultiSelect';

  const create = useMutation({
    mutationFn: (form: FieldForm) =>
      unwrap(
        tenantApi.POST('/api/v1/configuration/custom-fields', {
          body: {
            entity,
            key: form.key.trim(),
            type: form.type,
            label: form.label.trim(),
            options: selectable ? parseOptions(form.options) : [],
            isRequired: form.isRequired,
            sensitivity: form.sensitivity,
            showInQuickRegister: form.showInQuickRegister,
            sortOrder: Number(form.sortOrder) || 0,
            rowVersion: null,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['configuration', 'custom-fields'] });
      onClose();
    },
    onMutate: () => setFieldError(false),
    onError: (error) =>
      setFieldError(
        applyServerErrors(error, setError, {
          fields: ['key', 'label', 'type', 'options', 'sensitivity'],
          codeFields: { 'PHX-CFG-0301': 'key' },
        }) > 0,
      ),
  });

  const submit = handleSubmit((form) => {
    let ok = true;
    if (!KEY.test(form.key.trim())) {
      setError('key', { message: 'errors:validation.custom_field_key' });
      ok = false;
    }
    if (!form.label.trim()) {
      setError('label', { message: 'common:validation.required' });
      ok = false;
    }
    if (selectable && parseOptions(form.options).length === 0) {
      setError('options', { message: 'cfg:custom_fields.options_required' });
      ok = false;
    }
    if (ok) {
      create.mutate(form);
    }
  });

  return (
    <FormDialog
      open
      titleKey="cfg:custom_fields.add_title"
      screenId={SCR_STAFF_1202}
      onSubmit={() => void submit()}
      onClose={onClose}
      isDirty={formState.isDirty}
      isSubmitting={create.isPending}
      submitError={fieldError ? undefined : (create.error ?? undefined)}
    >
      <TextFieldRHF
        control={control}
        name="key"
        labelKey="cfg:custom_fields.key"
        helperKey="cfg:custom_fields.key_help"
        required
        fullWidth
        slotProps={{ htmlInput: { dir: 'ltr', maxLength: 64, 'data-testid': 'custom-field-key' } }}
      />
      <TextFieldRHF
        control={control}
        name="label"
        labelKey="cfg:custom_fields.label"
        required
        fullWidth
        slotProps={{ htmlInput: { maxLength: 100, 'data-testid': 'custom-field-label' } }}
      />
      <SelectRHF
        control={control}
        name="type"
        labelKey="cfg:custom_fields.type"
        fullWidth
        options={CUSTOM_FIELD_TYPES.map((value) => ({ value, label: t(`cfg:custom_fields.types.${value}`) }))}
      />
      {selectable && (
        <TextFieldRHF
          control={control}
          name="options"
          labelKey="cfg:custom_fields.options"
          helperKey="cfg:custom_fields.options_help"
          fullWidth
          multiline
          minRows={3}
        />
      )}
      <SelectRHF
        control={control}
        name="sensitivity"
        labelKey="cfg:custom_fields.sensitivity"
        helperKey="cfg:custom_fields.sensitivity_help"
        fullWidth
        options={CUSTOM_FIELD_SENSITIVITIES.map((value) => ({
          value,
          label: t(`cfg:custom_fields.sensitivities.${value}`),
        }))}
      />
      <CheckboxRHF control={control} name="isRequired" labelKey="cfg:custom_fields.required" />
      <CheckboxRHF control={control} name="showInQuickRegister" labelKey="cfg:custom_fields.quick_register" />
      <TextFieldRHF
        control={control}
        name="sortOrder"
        labelKey="cfg:custom_fields.sort_order"
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'numeric', maxLength: 4 } }}
      />
    </FormDialog>
  );
}

export function CustomFieldsPage() {
  const { t } = useTranslation('cfg');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const write = useWriteAccess('cfg.custom_fields.manage');
  const [entity, setEntity] = useState<CustomFieldEntity>('Patient');
  const [adding, setAdding] = useState(false);

  const fields = useQuery({
    queryKey: cfgKeys.customFields(session?.tenantId, entity),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/configuration/custom-fields', {
          params: { query: { entity, includeInactive: true } },
        }),
      ),
  });
  const setActive = useMutation({
    mutationFn: (field: CustomFieldDefinitionInfo) =>
      field.isActive
        ? unwrap(
            tenantApi.POST('/api/v1/configuration/custom-fields/{id}:deactivate', {
              params: { path: { id: field.id } },
            }),
          )
        : unwrap(
            tenantApi.POST('/api/v1/configuration/custom-fields/{id}:activate', {
              params: { path: { id: field.id } },
            }),
          ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['configuration', 'custom-fields'] }),
  });

  const columns: DataTableColumn<CustomFieldDefinitionInfo>[] = [
    { id: 'label', headerKey: 'cfg:custom_fields.label', cell: (row) => row.label },
    { id: 'key', headerKey: 'cfg:custom_fields.key', cell: (row) => <bdi dir="ltr">{row.key}</bdi> },
    {
      id: 'type',
      headerKey: 'cfg:custom_fields.type',
      cell: (row) => t(`cfg:custom_fields.types.${row.type}`),
    },
    {
      id: 'sensitivity',
      headerKey: 'cfg:custom_fields.sensitivity',
      cell: (row) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.sensitivity === 'Sensitive' ? 'warning' : 'default'}
          label={t(`cfg:custom_fields.sensitivities.${row.sensitivity}`)}
        />
      ),
    },
    {
      id: 'flags',
      headerKey: 'cfg:custom_fields.flags',
      cell: (row) => (
        <Stack direction="row" spacing={0.5}>
          {row.isRequired && <Chip size="small" label={t('cfg:custom_fields.required')} />}
          {row.showInQuickRegister && <Chip size="small" label={t('cfg:custom_fields.quick_register')} />}
        </Stack>
      ),
    },
    {
      id: 'status',
      headerKey: 'cfg:custom_fields.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'cfg:settings.actions',
      cell: (row) => (
        <Button
          size="small"
          onClick={() => setActive.mutate(row)}
          disabled={!write.canWrite || setActive.isPending}
          data-testid={`custom-field-toggle-${row.key}`}
        >
          {row.isActive ? t('cfg:custom_fields.deactivate') : t('cfg:custom_fields.activate')}
        </Button>
      ),
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_1202}
      titleKey="cfg:custom_fields.title"
      subtitleKey="cfg:custom_fields.subtitle"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAdding(true)}
          disabled={!write.canWrite}
          data-testid="custom-field-add"
        >
          {t('cfg:custom_fields.add')}
        </Button>
      }
      filters={
        <Stack spacing={1}>
          <WriteNotice write={write} />
          <TextField
            select
            size="small"
            label={t('cfg:custom_fields.entity')}
            value={entity}
            onChange={(event) => setEntity(event.target.value as CustomFieldEntity)}
            sx={{ maxWidth: 280 }}
            data-testid="custom-field-entity"
          >
            {CUSTOM_FIELD_ENTITIES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`cfg:custom_fields.entities.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          {setActive.error ? <ErrorState error={setActive.error} compact /> : null}
        </Stack>
      }
    >
      <DataTable
        captionKey="cfg:custom_fields.caption"
        columns={columns}
        rows={fields.data?.items}
        getRowId={(row) => row.id}
        isLoading={fields.isPending}
        error={fields.error}
        onRetry={() => void fields.refetch()}
        emptyTitleKey="cfg:custom_fields.empty"
      />
      {adding && <FieldDialog entity={entity} onClose={() => setAdding(false)} />}
    </ListPage>
  );
}
